import React from 'react';
import { cn } from '@/lib/utils';
import { Tag } from '@/components/ui/tag';

interface TagOverflowTooltipProps {
  label: string;
  values: string[];
  triggerLabel?: string;
  className?: string;
  itemClassName?: string;
  contentClassName?: string;
  showHeading?: boolean;
}

export const TagOverflowTooltip: React.FC<TagOverflowTooltipProps> = ({
  label,
  values,
  triggerLabel,
  className,
  itemClassName,
  contentClassName,
  showHeading = true,
}) => {
  const [open, setOpen] = React.useState(false);
  if (!values.length) return null;
  const displayTriggerLabel = triggerLabel || `+${values.length} more`;

  return (
    <span
      className="relative inline-flex min-w-0 shrink-0 align-middle"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
    >
      <Tag
        variant="outlined"
        size="xs"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        aria-label={`${displayTriggerLabel} ${label}: ${values.join(', ')}`}
        aria-expanded={open}
        className={cn(
          'min-h-5 rounded-md px-1.5 py-0.5 text-xs font-medium leading-tight hover:underline',
          className,
        )}
      >
        {displayTriggerLabel}
      </Tag>
      {open ? (
        <span
          role="tooltip"
          className={cn(
            'absolute right-0 top-full z-50 mt-1 flex max-h-36 w-max max-w-[min(18rem,calc(100vw-2rem))] flex-wrap gap-x-1 gap-y-0.5 overflow-y-auto rounded-md border bg-popover p-2 text-xs leading-snug text-popover-foreground shadow-md',
            contentClassName,
          )}
          onClick={(event) => event.stopPropagation()}
        >
          {showHeading ? (
            <span className="mb-0.5 block basis-full text-xs font-semibold uppercase leading-none tracking-normal text-muted-foreground">
              More {label}
            </span>
          ) : null}
          {values.map((value, index) => (
            <React.Fragment key={`${value}-${index}`}>
              <span
                className={cn(
                  'font-medium leading-snug text-foreground',
                  itemClassName,
                  'border-0 bg-transparent px-0 py-0',
                )}
              >
                {value}
              </span>
              {index < values.length - 1 ? (
                <span className={cn('font-medium leading-snug text-foreground', itemClassName, 'border-0 bg-transparent px-0 py-0')}>,</span>
              ) : null}
            </React.Fragment>
          ))}
        </span>
      ) : null}
    </span>
  );
};
